/**
 * backgroundProcessStatus — the single source of truth for background-process
 * status semantics.
 *
 * Status had been reasoned about with ad-hoc, slightly different string lists in
 * BackgroundProcessLauncher, BackgroundProcessSummaryPanel and
 * BackgroundProcessesButton (e.g. only some knew "inprogress"/"pending"). That
 * drift is a latent bug source. All status logic now lives here.
 *
 * Status is matched case-insensitively against the backend vocabulary. If the
 * backend introduces a new status, add it to exactly one of the sets below.
 */

/** The status vocabulary the backend emits. TODO: confirm/lock with backend. */
export type BackgroundProcessStatus =
    | "pending"
    | "running"
    | "inprogress"
    | "completed"
    | "success"
    | "failed"
    | "error";

const RUNNING = new Set<string>(["pending", "running", "inprogress"]);
const FINAL = new Set<string>(["completed", "success", "failed", "error"]);
const SUCCESS = new Set<string>(["completed", "success"]);

const norm = (status: string | undefined | null): string => (status ?? "").toLowerCase();

/** A terminal status — the process will not change further. */
export const isFinalStatus = (status: string | undefined | null): boolean => FINAL.has(norm(status));

/** A terminal status that finished successfully. */
export const isSuccessfulFinalStatus = (status: string | undefined | null): boolean =>
    SUCCESS.has(norm(status));

/** Still in flight (pending or actively running). */
export const isRunningStatus = (status: string | undefined | null): boolean => RUNNING.has(norm(status));

/** MUI palette key for chips/labels driven by status. */
export const statusPaletteColor = (
    status: string | undefined | null,
): "success" | "primary" | "error" | "default" => {
    if (isSuccessfulFinalStatus(status)) return "success";
    if (isRunningStatus(status)) return "primary";
    if (isFinalStatus(status)) return "error";
    return "default";
};

/** Hex colour for the same mapping, where a raw colour is needed (progress bars). */
export const statusHexColor = (status: string | undefined | null): string => {
    switch (statusPaletteColor(status)) {
        case "success":
            return "#2e7d32";
        case "primary":
            return "#0288d1";
        case "error":
            return "#d32f2f";
        default:
            return "inherit";
    }
};
